<template>
  <div class="pdf-viewer-root" >
    <div v-if="showToolbar" class="pdf-toolbar">
      <button type="button" class="toolbar-btn" :disabled="isLoading || currentPage <= 1" @click="prevPage">
        ‹
      </button>
      <span class="toolbar-label">{{ currentPage }} / {{ totalPages }}</span>
      <button type="button" class="toolbar-btn" :disabled="isLoading || currentPage >= totalPages" @click="nextPage">
        ›
      </button>

      <div class="toolbar-divider"></div>

      <button type="button" class="toolbar-btn" :disabled="isLoading" @click="zoomOut">−</button>
      <span class="toolbar-label">{{ Math.round(zoom * 100) }}%</span>
      <button type="button" class="toolbar-btn" :disabled="isLoading" @click="zoomIn">+</button>
    </div>

    <div ref="canvasWrap" class="pdf-canvas-wrap" :key="loadKey">
     
      <div v-if="isLoading" class="pdf-state">{{toLocal('general.loading_text')}}</div>
      <div v-else-if="errorMessage" class="pdf-state pdf-error">{{ errorMessage }}</div>
      <canvas v-show="!isLoading && !errorMessage" ref="pdfCanvas" class="pdf-canvas"></canvas>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf"
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.10.111/pdf.worker.min.js'

export default {
  name: 'PdfJsViewer',
  props: {
    src: {
      type: String,
      required: true,
    },
    initialPage: {
      type: Number,
      default: 1,
    },
    initialZoom: {
      type: Number,
      default: 1,
    },
    height: {
      type: String,
      default: '80vh',
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    fitToWidth: {
      type: Boolean,
      default: true,
    },
    withCredentials: {
      type: Boolean,
      default: false,
    },
    showDebug: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      zoom: 1,
      isLoading: false,
      _loadingTask: null,
      loadCounter: 0,
      activeLoadId: 0,
      loadKey: 0,
      errorMessage: '',
      pdfDoc: null,
      _renderTask: null

    };
  },

  watch: {
    src: {
        immediate: true,
        async handler() {
            await this.loadPdf();
        },
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.cancelRenderTask();
    if (this._loadingTask) {
      try {
        this._loadingTask.destroy();
      } catch (_error) {
        // no-op
      }
      this._loadingTask = null;
    }
    if (this.pdfDoc) {
      try {
        this.pdfDoc.destroy();
      } catch (_error) {
        // no-op
      }
      this.pdfDoc = null;
    }
  },
  methods: {
    async loadPdf() {
      this.cancelRenderTask();
      this.loadCounter += 1;
      const loadId = this.loadCounter;
      this.activeLoadId = loadId;
      this.loadKey += 1;
      // lifecycle debug
      if (this.pdfDoc) {
        try {
          this.pdfDoc.destroy();
        } catch (_error) {
          // no-op
        }
        this.pdfDoc = null;
      }

      if (this._loadingTask) {
        try {
          this._loadingTask.destroy();
        } catch (_error) {

          // no-op
        }
        this._loadingTask = null;
      }

      if (!this.src) {
        this.errorMessage = 'PDF source is missing.';
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';
      this.currentPage = this.initialPage;
      this.totalPages = 1;
      this.zoom = this.initialZoom;

       try {
            const normalizedUrl = encodeURI(this.src);
            const loadingTask =  pdfjsLib.getDocument(this.src);
            this.pdfDoc = await loadingTask.promise;
            this.totalPages = this.pdfDoc.numPages || 1;
            await this.$nextTick();
            await this.renderCurrentPage();
        } catch (error) {
            console.error('Failed to load PDF:', error);
            this.errorMessage = 'Nepavyko atidaryti PDF failo.';
            this.$emit('error', error);
        } finally {
          console.log('stopped loading')
            this.isLoading = false;
        }
    },
    async renderCurrentPage() {
      console.log(!this.pdfDoc || !this.$refs.pdfCanvas)
        if (!this.pdfDoc || !this.$refs.pdfCanvas) return;

        this.cancelRenderTask();

        const page = await this.pdfDoc.getPage(this.currentPage);
        const baseViewport = page.getViewport({ scale: 1 });

        let scale = this.zoom;
        if (this.fitToWidth && this.$refs.canvasWrap) {
            const padding = 24;
            const availableWidth = Math.max(this.$refs.canvasWrap.clientWidth - padding, 120);
            const fitScale = availableWidth / baseViewport.width;
            scale = fitScale * this.zoom;
        }

        const viewport = page.getViewport({ scale });
        const canvas = this.$refs.pdfCanvas;
        const context = canvas.getContext('2d');
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * devicePixelRatio);
        canvas.height = Math.floor(viewport.height * devicePixelRatio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

        this.renderTask = markRaw(
            page.render({
            canvasContext: context,
            viewport,
            }),
        );
        console.log(this.renderTask)
        await this.renderTask.promise;
        this.renderTask = null;
    },
    cancelRenderTask() {
      if (this._renderTask) {
        try {
          this._renderTask.cancel();
        } catch (_error) {
          // no-op
        }
        this._renderTask = null;
      }
    },
    async prevPage() {
      if (this.currentPage <= 1) return;
      this.currentPage -= 1;
      await this.renderCurrentPage();
      this.$emit('page-changed', this.currentPage);
    },
    async nextPage() {
      if (this.currentPage >= this.totalPages) return;
      this.currentPage += 1;
      await this.renderCurrentPage();
      this.$emit('page-changed', this.currentPage);
    },
    async zoomIn() {
      this.zoom = Math.min(this.zoom + 0.1, 3);
      await this.renderCurrentPage();
      this.$emit('zoom-changed', this.zoom);
    },
    async zoomOut() {
      this.zoom = Math.max(this.zoom - 0.1, 0.4);
      await this.renderCurrentPage();
      this.$emit('zoom-changed', this.zoom);
    },
    async handleResize() {
      if (!this.fitToWidth || this.isLoading || this.errorMessage) return;
      await this.renderCurrentPage();
    },
  },
};
</script>

<style scoped>
.pdf-viewer-root {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: white;
  overflow: hidden;
}

.pdf-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #eed4d470;
}

.toolbar-btn {
  border: 1px solid #d5c9ff;
  background: #fff;
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.toolbar-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toolbar-label {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #4d4b4b;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: #e8e2ff;
  margin: 0 4px;
}

.pdf-canvas-wrap {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12px;
}

.pdf-canvas {
  box-shadow: 0 6px 24px rgba(50, 30, 100, 0.12);
  border-radius: 6px;
  background: #fff;
}

.pdf-state {
  width: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  color: #4d4b4b;
}

.pdf-error {
  color: #c53b3b;
}
</style>
